import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Lightning, ShieldCheck, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiErrorDetail, API } from "@/lib/api";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { LogoPill } from "@/components/Logo";
import { AnimatedLogo } from "@/components/AnimatedLogo";

function homeForUser(u) {
  if (u.platform_role === "super_admin") return "/super-admin";
  if (u.role === "admin") return "/admin";
  return "/worker";
}

export default function Login() {
  const lastTenant = (() => { try { return JSON.parse(localStorage.getItem("fr_last_tenant") || "null"); } catch { return null; } })();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to={homeForUser(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Clear any prior impersonation on fresh login
      localStorage.removeItem("impersonate_tenant");
      localStorage.removeItem("impersonate_tenant_name");
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.name}`);
      nav(homeForUser(u));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-5">
      {/* left brand pane */}
      <div className="hidden lg:flex lg:col-span-3 relative bg-[#0f172a] text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxIVkFDJTIwdGVjaG5pY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzg1MDczMTE2fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#0f172a]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <LogoPill height={44}/>
          </div>
          <div>
            <h1 className="font-display font-black text-6xl leading-[0.95] tracking-tight">
              CLOCK IN.<br/>
              GET IT DONE.<br/>
              <span className="text-[#00C4CC]">SHIP THE WORK.</span>
            </h1>
            <p className="mt-6 text-lg max-w-lg text-slate-300 font-medium">
              The field-service dispatch platform for property crews. Jobs, checklists, photos, timesheets, receipts &amp; AI diagnostics — all in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Feature icon={<Lightning weight="bold" size={18}/>} label="GPS Clock-in"/>
              <Feature icon={<ShieldCheck weight="bold" size={18}/>} label="Before/After Proof"/>
              <Feature icon={<House weight="bold" size={18}/>} label="Multi-Issue Checklist"/>
            </div>
          </div>
        </div>
      </div>

      {/* right form pane */}
      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-[#f4f4f5]">
        <div className="w-full max-w-md">
          <div className="mb-6 flex justify-center" data-testid="login-lockup">
            <AnimatedLogo variant="lockup" height={96}/>
          </div>
          {lastTenant?.name && (
            <div className="mb-8 flex items-center justify-center gap-2" data-testid="login-cobrand">
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Signing in to</span>
              <span className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm">
                {lastTenant.logo && <img src={`${API}/tenant/logo/${lastTenant.id}`} alt="" className="h-6 w-6 object-contain"/>}
                <span className="text-xs font-black uppercase tracking-wider text-[#0B1C2D]">{lastTenant.name}</span>
              </span>
            </div>
          )}

          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-center">Sign in</h2>
          <p className="mt-2 text-slate-500 font-medium text-center">Access your crew dashboard</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="text-sm font-bold uppercase tracking-wide">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-2 w-full h-14 px-4 bg-white border-2 border-[#1f2937] focus:outline-none focus:ring-0 focus:border-[#00C4CC] font-medium"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase tracking-wide">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 w-full h-14 px-4 bg-white border-2 border-[#1f2937] focus:outline-none focus:ring-0 focus:border-[#00C4CC] font-medium"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="tactile-btn w-full h-16 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-display font-black text-lg uppercase tracking-wide disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <ForgotPasswordLink/>

          <p className="mt-6 text-center text-sm text-slate-500 font-medium">
            New here?{" "}
            <Link to="/signup" data-testid="login-to-signup" className="font-black uppercase tracking-wider text-[#00C4CC] underline">
              Start Your Free Company
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-2 border-white/60 bg-black/40">
      {icon}<span className="text-sm font-bold uppercase tracking-wide">{label}</span>
    </div>
  );
}

function ForgotPasswordLink() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await (await import("@/lib/api")).api.post("/auth/request-reset", { email: email.trim().toLowerCase() });
      toast.success("Reset request sent. Your admin will set you a new password.");
      setOpen(false); setEmail("");
    } catch (e) { toast.error("Please try again"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="forgot-password-link"
        className="mt-4 text-xs font-black uppercase tracking-wider text-[#00C4CC] underline">
        Forgot password?
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-display font-black text-xl">Request Password Reset</h3>
            <p className="mt-1 text-sm text-slate-600">Your admin will set a new password and share it with you.</p>
            <form onSubmit={submit} className="mt-4 space-y-3">
              <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
                data-testid="forgot-email" className="w-full h-11 px-3 border-2 border-[#0f172a]"/>
              <button type="submit" disabled={busy} data-testid="forgot-submit"
                className="tactile-btn w-full h-11 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider">
                {busy ? "Sending..." : "Send Reset Request"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
